// Order statistics and analytics hooks
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import { formatDateLocal, normalizeDateToMidnight } from '@/lib/utils'
import { isAssistantLoggedIn } from '@/lib/permissions'

export interface DailyStats {
  totalOrders: number
  totalRevenue: number
  averageOrder: number
  completedOrders: number
  pendingOrders: number
  inProgressOrders: number
  readyOrders: number
}

export interface PeriodStats {
  totalOrders: number
  totalRevenue: number
  averageOrder: number
  completedOrders: number
}

export interface ActiveOrder {
  id: string
  tableId: string
  tableNumber: number
  status: string
  createdAt: string
  itemCount: number
}

export interface OrderItem {
  name: string
  quantity: number
  price: number
  isCancelled?: boolean
}

export interface DetailedOrder {
  id: string
  tableNumber: number
  status: string
  createdAt: string
  completedAt: string | null
  totalAmount: number
  items: OrderItem[]
  cancellationConfirmedByName?: string | null
  serverName?: string | null
}

export interface DetailedOrdersResponse {
  orders: DetailedOrder[]
  totalCount: number
}

/**
 * Fetch daily order statistics
 */
export function useDailyOrderStats(date: Date, restaurantId: string | null) {
  const normalizedDate = normalizeDateToMidnight(date)
  const dateString = formatDateLocal(normalizedDate)
  
  return useQuery({
    queryKey: ['orderStats', 'daily', dateString, restaurantId],
    staleTime: 1 * 60 * 1000, // 1 minute - stats can change but not too frequently
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false, // Stats don't need real-time updates
    refetchOnMount: false,
    queryFn: async (): Promise<DailyStats> => {
      const emptyStats: DailyStats = {
        totalOrders: 0,
        totalRevenue: 0,
        averageOrder: 0,
        completedOrders: 0,
        pendingOrders: 0,
        inProgressOrders: 0,
        readyOrders: 0,
      }

      if (!restaurantId) {
        return emptyStats
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_get_daily_order_stats', {
          
          p_date: dateString,
          p_restaurant_id: restaurantId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have access to view dashboard')
          }
          throw error
        }

        if (!data || data.length === 0) return emptyStats
        const stats = data[0]
        if (!stats) return emptyStats

        return {
          totalOrders: Number(stats.total_orders) || 0,
          totalRevenue: Number(stats.total_revenue) || 0,
          averageOrder: Number(stats.average_order) || 0,
          completedOrders: Number(stats.completed_orders) || 0,
          pendingOrders: Number(stats.pending_orders) || 0,
          inProgressOrders: Number(stats.in_progress_orders) || 0,
          readyOrders: Number(stats.ready_orders) || 0,
        }
      }

      // Admin user - use direct RPC
      const { data, error } = await supabase.rpc('get_daily_order_stats', {
        p_date: dateString,
        p_restaurant_id: restaurantId,
      })

      if (error) {
        throw error
      }

      if (!data || data.length === 0) return emptyStats
      const stats = data[0]
      if (!stats) return emptyStats

      return {
        totalOrders: Number(stats.total_orders) || 0,
        totalRevenue: Number(stats.total_revenue) || 0,
        averageOrder: Number(stats.average_order) || 0,
        completedOrders: Number(stats.completed_orders) || 0,
        pendingOrders: Number(stats.pending_orders) || 0,
        inProgressOrders: Number(stats.in_progress_orders) || 0,
        readyOrders: Number(stats.ready_orders) || 0,
      }
    },
    enabled: !!restaurantId,
  })
}

/**
 * Fetch weekly order statistics
 */
export function useWeeklyOrderStats(weekStart: Date, restaurantId: string | null) {
  const normalizedDate = normalizeDateToMidnight(weekStart)
  const dateString = formatDateLocal(normalizedDate)

  return useQuery({
    queryKey: ['orderStats', 'weekly', dateString, restaurantId],
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<PeriodStats> => {
      const emptyStats: PeriodStats = {
        totalOrders: 0,
        totalRevenue: 0,
        averageOrder: 0,
        completedOrders: 0,
      }

      if (!restaurantId) {
        return emptyStats
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_get_weekly_order_stats', {
          
          p_week_start: dateString,
          p_restaurant_id: restaurantId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have access to view dashboard')
          }
          throw error
        }

        if (!data || data.length === 0) return emptyStats
        const stats = data[0]
        if (!stats) return emptyStats

        return {
          totalOrders: Number(stats.total_orders) || 0,
          totalRevenue: Number(stats.total_revenue) || 0,
          averageOrder: Number(stats.average_order) || 0,
          completedOrders: Number(stats.completed_orders) || 0,
        }
      }

      // Admin user - use direct RPC
      const { data, error } = await supabase.rpc('get_weekly_order_stats', {
        p_week_start: dateString,
        p_restaurant_id: restaurantId,
      })

      if (error) {
        throw error
      }

      if (!data || data.length === 0) return emptyStats
      const stats = data[0]
      if (!stats) return emptyStats

      return {
        totalOrders: Number(stats.total_orders) || 0,
        totalRevenue: Number(stats.total_revenue) || 0,
        averageOrder: Number(stats.average_order) || 0,
        completedOrders: Number(stats.completed_orders) || 0,
      }
    },
    enabled: !!restaurantId,
  })
}

/**
 * Fetch monthly order statistics
 */
export function useMonthlyOrderStats(month: number, year: number, restaurantId: string | null) {
  return useQuery({
    queryKey: ['orderStats', 'monthly', month, year, restaurantId],
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<PeriodStats> => {
      const emptyStats: PeriodStats = {
        totalOrders: 0,
        totalRevenue: 0,
        averageOrder: 0,
        completedOrders: 0,
      }

      if (!restaurantId) {
        return emptyStats
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_get_monthly_order_stats', {
          
          p_month: month,
          p_year: year,
          p_restaurant_id: restaurantId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have access to view dashboard')
          }
          throw error
        }

        if (!data || data.length === 0) return emptyStats
        const stats = data[0]
        if (!stats) return emptyStats

        return {
          totalOrders: Number(stats.total_orders) || 0,
          totalRevenue: Number(stats.total_revenue) || 0,
          averageOrder: Number(stats.average_order) || 0,
          completedOrders: Number(stats.completed_orders) || 0,
        }
      }

      // Admin user - use direct RPC
      const { data, error } = await supabase.rpc('get_monthly_order_stats', {
        p_month: month,
        p_year: year,
        p_restaurant_id: restaurantId,
      })

      if (error) {
        throw error
      }

      if (!data || data.length === 0) return emptyStats
      const stats = data[0]
      if (!stats) return emptyStats

      return {
        totalOrders: Number(stats.total_orders) || 0,
        totalRevenue: Number(stats.total_revenue) || 0,
        averageOrder: Number(stats.average_order) || 0,
        completedOrders: Number(stats.completed_orders) || 0,
      }
    },
    enabled: !!restaurantId,
  })
}

/**
 * Fetch order statistics for a custom date range
 * Used for "last N days" type queries
 */
export function useDateRangeOrderStats(
  restaurantId: string | null,
  startDate: Date,
  endDate: Date
) {
  const normalizedStart = normalizeDateToMidnight(startDate)
  const startDateString = formatDateLocal(normalizedStart)
  const endDateString = formatDateLocal(endDate)

  return useQuery({
    queryKey: ['orderStats', 'dateRange', restaurantId, startDateString, endDateString],
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<PeriodStats> => {
      const emptyStats: PeriodStats = {
        totalOrders: 0,
        totalRevenue: 0,
        averageOrder: 0,
        completedOrders: 0,
      }

      if (!restaurantId) {
        return emptyStats
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_get_date_range_order_stats', {
          
          p_restaurant_id: restaurantId,
          p_start_date: startDateString,
          p_end_date: endDateString,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have access to view dashboard')
          }
          throw error
        }

        if (!data || data.length === 0) return emptyStats
        const stats = data[0]
        if (!stats) return emptyStats

        return {
          totalOrders: Number(stats.total_orders) || 0,
          totalRevenue: Number(stats.total_revenue) || 0,
          averageOrder: Number(stats.average_order) || 0,
          completedOrders: Number(stats.completed_orders) || 0,
        }
      }

      // Admin user - use direct RPC
      const { data, error } = await supabase.rpc('get_date_range_order_stats', {
        p_restaurant_id: restaurantId,
        p_start_date: startDateString,
        p_end_date: endDateString,
      })

      if (error) {
        throw error
      }

      if (!data || data.length === 0) return emptyStats
      const stats = data[0]
      if (!stats) return emptyStats

      return {
        totalOrders: Number(stats.total_orders) || 0,
        totalRevenue: Number(stats.total_revenue) || 0,
        averageOrder: Number(stats.average_order) || 0,
        completedOrders: Number(stats.completed_orders) || 0,
      }
    },
    enabled: !!restaurantId,
  })
}

/**
 * Fetch active orders (pending and in progress)
 */
export function useActiveOrders(restaurantId: string | null) {
  return useQuery({
    queryKey: queryKeys.orders.list({ restaurantId, active: true }),
    staleTime: 30 * 1000, // 30 seconds - active orders change frequently
    gcTime: 2 * 60 * 1000, // 2 minutes cache
    refetchOnWindowFocus: false, // Use realtime for orders
    refetchOnMount: false,
    queryFn: async (): Promise<ActiveOrder[]> => {
      if (!restaurantId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_get_active_orders', {
          
          p_restaurant_id: restaurantId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have access to view dashboard')
          }
          throw error
        }

        return (data || []).map((order: any) => ({
          id: order.id,
          tableId: order.table_id,
          tableNumber: order.table_number,
          status: order.status,
          createdAt: order.created_at,
          itemCount: Number(order.item_count) || 0,
        }))
      }

      // Admin user - use direct RPC
      const { data, error } = await supabase.rpc('get_active_orders', {
        p_restaurant_id: restaurantId,
      })

      if (error) {
        throw error
      }

      return (data || []).map((order: any) => ({
        id: order.id,
        tableId: order.table_id,
        tableNumber: order.table_number,
        status: order.status,
        createdAt: order.created_at,
        itemCount: Number(order.item_count) || 0,
      }))
    },
    enabled: !!restaurantId,
    // Real-time updates handled by useOrders subscriptions
  })
}

/**
 * Fetch all detailed orders for a date range (no server-side pagination).
 * Pagination and filtering is handled client-side by OrdersTable.
 */
export function useDetailedOrders(
  restaurantId: string | null,
  startDate: Date,
  endDate: Date
) {
  const normalizedStart = normalizeDateToMidnight(startDate)
  const startDateString = formatDateLocal(normalizedStart)
  const endDateString = formatDateLocal(endDate)

  return useQuery({
    queryKey: [
      'orderStats',
      'detailed',
      restaurantId,
      startDateString,
      endDateString,
    ],
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<DetailedOrdersResponse> => {
      if (!restaurantId) {
        return { orders: [], totalCount: 0 }
      }

      // Fetch all orders for the date range (large limit, no offset)
      const fetchLimit = 99999
      const fetchOffset = 0

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_get_detailed_orders', {
          p_restaurant_id: restaurantId,
          p_start_date: startDateString,
          p_end_date: endDateString,
          p_limit: fetchLimit,
          p_offset: fetchOffset,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have access to view dashboard')
          }
          throw error
        }

        if (!data || data.length === 0) {
          return { orders: [], totalCount: 0 }
        }

        const totalCount = data[0]?.total_count ? Number(data[0].total_count) : 0

        const orders: DetailedOrder[] = data.map((order: any) => ({
          id: order.id,
          tableNumber: order.table_number,
          status: order.status,
          createdAt: order.created_at,
          completedAt: order.completed_at,
          totalAmount: Number(order.total_amount) || 0,
          items: order.items || [],
          cancellationConfirmedByName: order.cancellation_confirmed_by_name || null,
          serverName: order.server_name || null,
        }))

        return { orders, totalCount }
      }

      // Admin user - use direct RPC
      const { data, error } = await supabase.rpc('get_detailed_orders', {
        p_restaurant_id: restaurantId,
        p_start_date: startDateString,
        p_end_date: endDateString,
        p_limit: fetchLimit,
        p_offset: fetchOffset,
      })

      if (error) {
        throw error
      }

      if (!data || data.length === 0) {
        return { orders: [], totalCount: 0 }
      }

      const totalCount = data[0]?.total_count ? Number(data[0].total_count) : 0

      const orders: DetailedOrder[] = data.map((order: any) => ({
        id: order.id,
        tableNumber: order.table_number,
        status: order.status,
        createdAt: order.created_at,
        completedAt: order.completed_at,
        totalAmount: Number(order.total_amount) || 0,
        items: order.items || [],
        cancellationConfirmedByName: order.cancellation_confirmed_by_name || null,
        serverName: order.server_name || null,
      }))

      return { orders, totalCount }
    },
    enabled: !!restaurantId,
  })
}
